import { useEffect, useMemo, useState } from "react"
import { formatCoverTitle, marqueeConfig, marqueeCoverUrls } from "./data/marquee-covers-data"
import { useMarqueeAnimation } from "./hooks/use-marquee-animation"
import { useMediaQuery } from "./hooks/use-media-query"

interface MarqueeProps {
  /** Desired scroll speed in pixels per second (default 100) */
  speed?: number
}

export function LandingBracketSectionCoverMarquee({ speed = marqueeConfig.defaultSpeed }: MarqueeProps) {
  // Repeat covers dynamically to ensure first strip wide enough
  const [repeatCount, setRepeatCount] = useState(2)

  const images = useMemo(() => {
    const arr: string[] = []
    for (let i = 0; i < repeatCount * 2; i++) {
      arr.push(...marqueeCoverUrls)
    }
    return arr.map((url, idx) => ({
      url,
      title: formatCoverTitle(url),
      uniqueKey: `cover-${formatCoverTitle(url)}-${idx}`,
    }))
  }, [repeatCount])

  const { marqueeRef, coverHeight } = useMarqueeAnimation(speed, repeatCount)

  // Arc amplitude derived from cover height (35% of height).
  // Disabled for mobile (viewport < 640px) so covers render in straight rows.
  const isMobile = useMediaQuery("(max-width: 639px)")
  const arcAmplitude = isMobile ? 0 : coverHeight * marqueeConfig.arcRatio

  // Ensure strip width exceeds 2x viewport
  useEffect(() => {
    function checkWidth() {
      if (!marqueeRef.current) return
      const firstImg = marqueeRef.current.querySelector("img") as HTMLImageElement | null
      const cardWidth = firstImg ? firstImg.offsetWidth + 8 /*mr-2*/ : 80
      const requiredWidth = window.innerWidth * 2
      const minRepeat = Math.ceil(requiredWidth / (cardWidth * marqueeCoverUrls.length))
      if (minRepeat > repeatCount) {
        setRepeatCount(minRepeat)
      }
    }

    checkWidth()
    window.addEventListener("resize", checkWidth)
    return () => window.removeEventListener("resize", checkWidth)
  }, [repeatCount, marqueeRef])

  return (
    <div
      className="relative w-full overflow-visible mt-0 select-none pointer-events-none"
      style={{
        paddingBottom: `${arcAmplitude}px`,
        minHeight: `${coverHeight + arcAmplitude}px`,
        pointerEvents: "none",
      }}
    >
      {/* Additional wrapper hides horizontal overflow but allows vertical overflow */}
      <div className="overflow-x-hidden w-full" style={{ overflowY: "visible" }}>
        {/* Pure-CSS marquee animation for buttery-smooth scrolling */}
        <div ref={marqueeRef} className="flex items-center whitespace-nowrap">
          {images.map(({ url, title, uniqueKey }, idx) => {
            // Calculate vertical offset to give a curved "smile" shape
            const baseLength = marqueeCoverUrls.length
            const mid = (baseLength - 1) / 2
            const relativeIdx = (idx % baseLength) - mid // range ~ [-mid, mid]
            const curvatureFactor = 1 - (relativeIdx / mid) ** 2 // 1 at center → 0 at edges
            const offsetY = isMobile ? 0 : arcAmplitude * curvatureFactor // Disable wave on mobile

            const { coverSizes } = marqueeConfig
            const sizeClasses = `${coverSizes.mobile} ${coverSizes.sm} ${coverSizes.md} ${coverSizes.mdlg} ${coverSizes.lg} ${coverSizes.xl}`

            return (
              <div
                key={uniqueKey}
                className="relative mr-2 last:mr-0 select-none shrink-0"
                style={{
                  transform: `translateY(${offsetY}px)`,
                }}
              >
                <img
                  src={url}
                  alt={title}
                  className={`${sizeClasses} rounded-xl object-cover shrink-0`}
                  loading="lazy"
                  decoding="async"
                  width={96}
                  height={96}
                />
                {/* Scanlines overlay */}
                <div className="absolute inset-0 rounded-xl pointer-events-none scanlines opacity-50 mix-blend-overlay" />
                <span className="pointer-events-none absolute top-full left-1/2 -translate-x-1/2 mt-1 text-xs md:text-sm font-medium text-gray-100 opacity-0">
                  {title}
                </span>
              </div>
            )
          })}
        </div>
        {/* Invisible spacer to guarantee extra vertical room underneath the wave */}
        <div style={{ height: arcAmplitude }} aria-hidden="true" />
      </div>
    </div>
  )
}
